import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useMemo, useRef, useState } from "react";
import AutoSizer, { Size } from "react-virtualized-auto-sizer";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Label,
  Tooltip as RechartsTooltip,
  RectangleProps,
  ReferenceLine,
  XAxis,
  YAxis,
} from "recharts";

import { usePricingEstimatorContext } from "..";
import { formatPricePerCarat, numberWithCommas } from "../../../../tools";

const PLOT_CONFIG = [
  { fill: "#A6CFC3ff", range: [40, 60] },
  { fill: "#5CAE95ff", range: [45, 55] },
  { fill: "#118C67ff", range: [49, 51] },
] as const;

const HorizontalBar = (props: RectangleProps) => {
  const { x, y, width } = props;
  if (x == null || y == null || width == null) {
    return null;
  }

  return (
    <line
      x1={x}
      y1={y}
      x2={x + width}
      y2={y}
      stroke="#000000"
      strokeWidth={1}
    />
  );
};

type SchemePlotData = {
  id: string;
  system: string;
  color: string;
  percentiles: number[];
  toP40: number;
  toP45: number;
  toP49: number;
  toP50: number;
  p50: number;
  toP51: number;
  toP55: number;
  toP60: number;
};

const Tooltip = (props: Record<string, any> & { data: SchemePlotData[] }) => {
  const { active, payload } = props;
  const isVisible = active && payload && payload.length;

  if (!isVisible) {
    return null;
  }

  const data = props.data.find((item) => item.id === props.label);
  if (!data) {
    return null;
  }

  return (
    <Stack
      direction="column"
      sx={{
        backgroundColor: "rgba(97, 97, 97, 0.92)",
        borderRadius: "4px",
        color: "white",
        paddingX: 1,
        paddingY: 0.5,
      }}
    >
      <Typography variant="subtitle1">Percentiles 40-60</Typography>
      <Typography variant="subtitle2">
        {formatPricePerCarat(data.percentiles[39])} &rarr;{" "}
        {formatPricePerCarat(data.percentiles[59])}
      </Typography>
    </Stack>
  );
};

type MouseState = {
  chartX?: number;
  chartY?: number;
};

export function PricingGraphSkeleton() {
  return <Skeleton variant="rounded" width={"100%"} height={"100%"} />;
}

export function PricingGraph() {
  const chartRef = useRef(null);

  const {
    displayNormalizedPricesState,
    mutationResult,
    submittedFormValuesState,
  } = usePricingEstimatorContext();

  const [tooltipIsActive, setTooltipIsActive] = useState(false);
  const [mouseState, setMouseState] = useState<MouseState | null>(null);

  const referenceLineValue: number | undefined = useMemo(() => {
    const chart = chartRef.current as any;
    if (!chart) return;

    // Find our YAxis instance
    const yAxis =
      chart.state.yAxisMap && Object.values(chart.state.yAxisMap)[0];
    if (!yAxis || typeof yAxis.scale !== "function") return;

    // Invert the scale: pixel → data value
    const dataValue = yAxis.scale.invert(mouseState?.chartY);
    return dataValue;
  }, [chartRef, mouseState]);

  const selectedPricingFactors = submittedFormValuesState[0]?.factors ?? [];
  const displayNormalizedPrice = displayNormalizedPricesState[0];
  const denormalizationFactor = selectedPricingFactors.reduce(
    (acc, factor) => acc * factor.factor,
    1,
  );

  const data =
    mutationResult.data?.estimateDiamondPrice.schemesPercentiles.map(
      (scheme) => {
        const percentiles = scheme.percentiles;

        const adjustedPercentiles = displayNormalizedPrice
          ? percentiles
          : percentiles.map((p) => p * denormalizationFactor);

        const ticks = {
          p40: adjustedPercentiles[39],
          p45: adjustedPercentiles[44],
          p49: adjustedPercentiles[48],
          p50: adjustedPercentiles[49],
          p51: adjustedPercentiles[50],
          p55: adjustedPercentiles[54],
          p60: adjustedPercentiles[59],
        };
        return {
          id: `${scheme.equivalentColors.system}-${scheme.equivalentColors.color}`,
          system: scheme.equivalentColors.system,
          color: scheme.equivalentColors.color,
          percentiles: adjustedPercentiles,
          toP40: ticks.p40,
          toP45: ticks.p45 - ticks.p40,
          toP49: ticks.p49 - ticks.p45,
          toP50: ticks.p50 - ticks.p49,
          p50: 0,
          toP51: ticks.p51 - ticks.p50,
          toP55: ticks.p55 - ticks.p51,
          toP60: ticks.p60 - ticks.p55,
        };
      },
    ) ?? [];

  // TODO: adjust this value dynamically based on data
  const yAxisWidth = 100;
  return (
    <AutoSizer>
      {({ width, height }: Size) => (
        <ComposedChart
          ref={chartRef}
          width={width}
          height={height}
          data={data}
          onMouseMove={(mouseState: MouseState) => setMouseState(mouseState)}
        >
          {/* this enables applying a background on svg text elements */}
          <defs>
            <filter x="-0.1" y="-0.1" width="1.2" height="1.2" id="solid">
              <feFlood floodColor="red" result="bg" />
              <feMerge>
                <feMergeNode in="bg" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          <CartesianGrid strokeDasharray="3 3" />

          <XAxis
            type="category"
            dataKey="system"
            orientation="top"
            ticks={data.map((d) => d.system)}
            xAxisId="system"
          />
          <XAxis
            type="category"
            dataKey="color"
            orientation="bottom"
            ticks={data.map((d) => d.color)}
            xAxisId="color"
          />

          {/* this hidden XAxis is a hack to receive the id of the SchemePlotData that is hovered by the mouse cursor */}
          <XAxis type="category" dataKey="id" hide />

          <YAxis
            width={yAxisWidth}
            tickFormatter={numberWithCommas}
            label={{
              value: "$/ct",
              style: { textAnchor: "middle" },
              angle: -90,
              position: "left",
              offset: 0,
            }}
          />

          {referenceLineValue && (
            <ReferenceLine y={referenceLineValue} stroke="red">
              <Label
                content={(props: any) => {
                  return (
                    <text
                      x={props.viewBox.x}
                      y={props.viewBox.y}
                      filter="url(#solid)"
                      textAnchor="end"
                      transform="translate(-8, 5)"
                      fill="white"
                    >
                      {formatPricePerCarat(referenceLineValue)}
                    </text>
                  );
                }}
              />
            </ReferenceLine>
          )}

          <Bar barSize={40} stackId={"a"} dataKey={"toP40"} fill={"none"} />
          <Bar
            stackId={"a"}
            dataKey={"toP45"}
            isAnimationActive={false}
            fill={PLOT_CONFIG[0].fill}
            onMouseEnter={() => setTooltipIsActive(true)}
            onMouseOut={() => setTooltipIsActive(false)}
          />
          <Bar
            stackId={"a"}
            dataKey={"toP49"}
            isAnimationActive={false}
            fill={PLOT_CONFIG[1].fill}
            onMouseEnter={() => setTooltipIsActive(true)}
            onMouseOut={() => setTooltipIsActive(false)}
          />
          <Bar
            stackId={"a"}
            dataKey={"toP50"}
            isAnimationActive={false}
            fill={PLOT_CONFIG[2].fill}
            onMouseEnter={() => setTooltipIsActive(true)}
            onMouseOut={() => setTooltipIsActive(false)}
          />
          <Bar
            stackId={"a"}
            dataKey={"p50"}
            isAnimationActive={false}
            shape={<HorizontalBar />}
            onMouseEnter={() => setTooltipIsActive(true)}
            onMouseOut={() => setTooltipIsActive(false)}
          />
          <Bar
            stackId={"a"}
            dataKey={"toP51"}
            isAnimationActive={false}
            fill={PLOT_CONFIG[2].fill}
            onMouseEnter={() => setTooltipIsActive(true)}
            onMouseOut={() => setTooltipIsActive(false)}
          />
          <Bar
            stackId={"a"}
            dataKey={"toP55"}
            isAnimationActive={false}
            fill={PLOT_CONFIG[1].fill}
            onMouseEnter={() => setTooltipIsActive(true)}
            onMouseOut={() => setTooltipIsActive(false)}
          />
          <Bar
            stackId={"a"}
            dataKey={"toP60"}
            isAnimationActive={false}
            fill={PLOT_CONFIG[0].fill}
            onMouseEnter={() => setTooltipIsActive(true)}
            onMouseOut={() => setTooltipIsActive(false)}
          />
          <RechartsTooltip
            active={tooltipIsActive}
            content={<Tooltip data={data} />}
          />
        </ComposedChart>
      )}
    </AutoSizer>
  );
}
